/**
 * RealBackend — adaptador WebSocket que substitui o SimulatedBackend.
 *
 * Tem a MESMA interface usada pelo PlayerService (constructor(agents),
 * setTarget, tick) — é um drop-in. Em vez de inventar a dinâmica, recebe
 * comandos do servidor (spawn/goto/action/emote/say/leave) e os aplica ao
 * CityAgents; e traduz os eventos do CityAgents (parked/wandering/exited) em
 * telemetria de volta ao servidor (arrived/wandering/left).
 *
 * Navegação é client-authoritative: o servidor manda intenções, este adaptador
 * só as encaminha. Veja docs/protocol.md e shared/protocol.js (contrato v1).
 *
 * Plumbing: conexão + reconexão com backoff, heartbeat (responde ping → pong e
 * vigia silêncio do servidor), seq de saída, validação leve de cada mensagem.
 */
import * as proto from '../../shared/protocol.js';
import { BUILDING_IDS, BUILDING_INDEX, PLAZA_ID, strollDistrictOf } from '../world/cityLayout.js';

const { MSG } = proto;

const RECONNECT_BASE = 600;       // ms — 1ª espera de reconexão
const RECONNECT_MAX = 8000;       // ms — teto do backoff exponencial
const RECONNECT_JITTER = 0.3;     // ±30% sorteado sobre a espera (ver _scheduleReconnect)
// Silêncio do servidor que dispara a reconexão. O gateway pinga a cada 8 s, então 15 s
// derrubava a sessão ao PRIMEIRO ping perdido — e num telão de 8 h um ping se perde por
// muito menos que um problema real (pausa de GC, rajada de broadcast). Com 26 s são dois
// pings inteiros de folga antes de considerar a sessão morta.
const HEARTBEAT_TIMEOUT = 26000;
// Handshake que não abre nem fecha (rede em buraco negro): sem prazo, `_connecting` ficava
// preso e nenhuma nova tentativa acontecia.
const CONNECT_TIMEOUT = 12000;
const DEBUG = typeof location !== 'undefined' && /[?&]debug\b/.test(location.search);

export class RealBackend {
  constructor(agents, options = {}) {
    this.agents = agents;
    this.url = options.url || defaultUrl();
    this.city = options.city || null;
    this.token = options.token || viewerToken(); // VIEWER_TOKEN (se o display for protegido)
    this._WS = options.WebSocketImpl || (typeof WebSocket !== 'undefined' ? WebSocket : null);
    // tela que só ASSISTE (o /me do jogador): recebe tudo, não reporta nada (ver _onAgentEvent)
    this.spectator = !!options.spectator;
    // hooks p/ a UI (emote/say no EntityManager, score no HUD, conquista no banner)
    this._effects = { onEmote: options.onEmote || null, onSay: options.onSay || null, onScore: options.onScore || null, onRestyle: options.onRestyle || null, onAchievement: options.onAchievement || null, onAnnounce: options.onAnnounce || null };

    this._ws = null;
    this._open = false;
    this._connecting = false;
    this._closedByUser = false;
    this._sessionId = null;

    this._inbox = [];           // mensagens recebidas, aplicadas no próximo tick
    this._outbox = [];          // frames pendentes enquanto o socket não abre
    this._outSeq = 0;           // seq de saída (monotônico)
    this._inSeq = null;         // último seq recebido (detecção de buraco)

    this._target = 0;           // população desejada (servidor é autoritativo)
    this._reconnectAt = 0;
    this._reconnectDelay = RECONNECT_BASE;
    this._lastInbound = 0;

    this._pendingSpawns = [];   // specs que não entraram (via lotada) — re-tenta
    this._parked = new Set();   // ids atualmente estacionados (p/ aplicar action)
    this._pendingAction = new Map(); // action recebida antes de estacionar
    this._pendingGoto = new Map();   // goto recebido enquanto o jogador ainda está na FILA de entrada
    // Ids que ESTE adaptador colocou na cidade (ou na fila de entrada) — o CityAgents não
    // enumera os seus, e só nós criamos avatares no modo ao vivo. É a base do expurgo de
    // FANTASMAS no snapshot (_purgeGhosts). Guarda a GERAÇÃO da conexão em que o avatar
    // nasceu: quem nasceu na conexão ATUAL nunca é fantasma (spawn em voo).
    this._live = new Map();     // id → geração (_gen) em que foi criado localmente
    this._gen = 0;              // +1 a cada socket ABERTO (ver _openSocket)
    // Último destino aplicado por id — o snapshot da reconexão compara com ele para saber
    // o que de fato mudou enquanto esta tela esteve fora (ver _reconciliar).
    this._destino = new Map();
    // "Visão do jogador" (?follow=<key>): índice chave-opaca ↔ id interno. O id muda a
    // cada reentrada, mas o followKey (derivado do externalId) é estável → a câmera
    // re-adquire o avatar pela chave. Ver resolveFollow().
    this._keyById = new Map(); // id → followKey
    this._idByKey = new Map(); // followKey → id (mais recente vence)

    // eventos do CityAgents → telemetria p/ o servidor
    agents.onEvent((type, a) => this._onAgentEvent(type, a));

    if (options.autoConnect !== false) this.connect();
  }

  /* --------- interface usada pelo PlayerService (igual ao SimulatedBackend) --------- */

  /** Servidor decide quem entra/sai; guardamos a meta só por compatibilidade. */
  setTarget(n) {
    this._target = Math.max(0, Math.round(n));
  }

  /** Chamado a cada tick pelo PlayerService (now = performance.now()). */
  tick(now) {
    // MESMO RELÓGIO. Os prazos internos (`_lastInbound`, `_connectAt`, `_reconnectAt`) são
    // carimbados com `_now()` = `performance.now()`, que conta desde a abertura da página.
    // Um chamador que passasse `Date.now()` (epoch) faria toda subtração dar ~1,7e12 ms:
    // os dois vigias disparariam em TODO tick e o telão entraria num laço de reconexão
    // permanente — e silencioso, porque cada tentativa parece legítima. Uma linha resolve.
    const agora = this._now();
    if (!Number.isFinite(now) || Math.abs(now - agora) > 60000) now = agora;
    // 1) vigia silêncio do servidor → força reconexão
    if (this._open && this._lastInbound && now - this._lastInbound > HEARTBEAT_TIMEOUT) {
      this._reconnect('heartbeat-timeout');
    }
    // 1b) conexão PENDURADA: o `_connecting` bloqueia toda nova tentativa, e um socket que
    //     nunca abre nem fecha (rede em buraco negro — o WiFi do quiosque some sem RST)
    //     deixava o telão parado indefinidamente. MEDIDO: 1 tentativa em 10 minutos.
    if (this._connecting && this._connectAt && now - this._connectAt > CONNECT_TIMEOUT) {
      this._reconnect('handshake-travado');
    }
    // 2) (re)conecta quando chegar a hora
    if (!this._open && !this._connecting && !this._closedByUser && this._WS && now >= this._reconnectAt) {
      this._openSocket();
    }
    // 3) aplica as mensagens recebidas desde o último tick — ANTES do retry de entrada (4).
    //    Ordem importa: um LEAVE que chega logo após o SPAWN precisa PURGAR o pendente
    //    ANTES que o retry o coloque na cidade (senão o jogador entraria e só depois sairia).
    //    Cada msg em try/catch → uma malformada não descarta as SPAWNs seguintes do lote.
    if (this._inbox.length) {
      const batch = this._inbox;
      this._inbox = [];
      for (const m of batch) {
        try { this._apply(m); }
        catch (e) { console.warn('[RealBackend] erro ao aplicar', m && m.type, e); }
      }
    }
    // 4) re-tenta INDEFINIDAMENTE as entradas que pegaram a "boca" da via lotada. O front
    //    SEMPRE coloca todo jogador do backend na cidade — mesmo que o trânsito atrase a
    //    entrada: NUNCA desiste (antes desistia após ~5s e o jogador só entrava num refresh).
    //    As bocas liberam conforme os carros entram e se espalham, então o pendente drena.
    if (this._pendingSpawns.length) {
      const still = [];
      for (const s of this._pendingSpawns) {
        if (this.agents.has && this.agents.has(s.id)) continue; // já entrou (outro caminho) → descarta
        let entered = false;
        try { entered = this.agents.spawn(s) !== false; } // try/catch: um spawn degenerado não derruba o tick
        catch (e) { console.warn('[RealBackend] erro ao entrar', s && s.id, e); entered = true; /* descarta (não re-tenta em loop) */ }
        if (!entered) { still.push(s); continue; } // boca ainda lotada → tenta de novo no próximo tick
        if (this._pendingGoto.has(s.id)) {          // entrou → aplica o destino que ficou guardado
          const g = this._pendingGoto.get(s.id);
          this._pendingGoto.delete(s.id);
          this._applyGoto({ id: s.id, building: g.building, mode: g.mode });
        }
      }
      this._pendingSpawns = still;
    }
  }

  /* ------------------------------ conexão ------------------------------ */

  connect() {
    this._closedByUser = false;
    this._reconnectDelay = RECONNECT_BASE;
    this._reconnectAt = 0;
    this._openSocket();
  }

  disconnect() {
    this._closedByUser = true;
    this._open = false;
    if (this._ws) { try { this._ws.close(); } catch (e) { /* ignora */ } }
    this._ws = null;
  }

  /** Liga emote/say ao renderer (chamado pelo PlayerService.setEffects). */
  setEffects({ onEmote, onSay, onScore, onRestyle, onAchievement, onAnnounce } = {}) {
    if (onEmote) this._effects.onEmote = onEmote;
    if (onSay) this._effects.onSay = onSay;
    if (onScore) this._effects.onScore = onScore;
    if (onRestyle) this._effects.onRestyle = onRestyle;
    if (onAchievement) this._effects.onAchievement = onAchievement;
    if (onAnnounce) this._effects.onAnnounce = onAnnounce;
  }

  _openSocket() {
    if (!this._WS) { console.warn('[RealBackend] WebSocket indisponível neste ambiente'); return; }
    this._connecting = true;
    this._connectAt = this._now(); // prazo do handshake — ver o watchdog no tick()
    let ws;
    try {
      ws = new this._WS(this.url);
    } catch (e) {
      this._connecting = false;
      this._scheduleReconnect('construtor');
      return;
    }
    this._ws = ws;
    // GUARDA DE IDENTIDADE em todos os handlers. Os eventos de um socket ANTIGO chegam
    // atrasados (o `onclose` do browser demora ~2 s quando o servidor está lento) e, sem
    // esta checagem, o `onclose` de A executava sobre a conexão B — que estava ABERTA e
    // saudável — marcando `_open = false` e agendando outra reconexão. Como o welcome de
    // cada nova conexão zera o backoff, o laço se auto-alimentava: MEDIDO, um único
    // soluço de 15 s no gateway virava 225 reconexões por hora, 1.799 sockets e 156 MB de
    // snapshot em 8 h — com DUAS sessões do mesmo telão vivas o tempo todo, então cada
    // conquista saía em DOIS banners e cada balão em dobro.
    const meu = () => this._ws === ws;
    ws.onopen = () => {
      if (!meu()) { try { ws.close(); } catch { /* já foi */ } return; } // socket órfão
      this._connecting = false;
      this._open = true;
      this._gen++; // nova conexão: tudo que já existe localmente vira "geração anterior"
      this._lastInbound = this._now();
      // O BACKOFF NÃO É ZERADO AQUI. Abrir o socket não prova que a sessão presta: um
      // servidor que ACEITA e FECHA logo em seguida — VIEWER_TOKEN rotacionado no deploy,
      // o server.close() do shutdown drenando, o `terminate` do resume ou o kill do
      // heartbeat — renascia o passo em RECONNECT_BASE a cada tentativa e o backoff nunca
      // crescia: martelo fixo de ~1,6 handshake/s POR TELA, indefinidamente (medido).
      // Com as ~200 telas /me de uma cidade isso é uma tempestade de handshakes e de
      // snapshots (~89 KiB cada) sem nenhuma tela estabilizar. O reset só vem quando a
      // sessão RENDE de fato — ver _sessionEstablished(), chamado no welcome/snapshot.
      // handshake (com resume se já tivemos sessão)
      const resume = this._sessionId ? { sessionId: this._sessionId, lastSeq: this._inSeq } : null;
      this._send(proto.hello({ city: this.city, resume, token: this.token }));
      this._flushOutbox();
    };
    ws.onmessage = (ev) => { if (meu()) this._onMessage(ev.data); };
    ws.onclose = () => {
      if (!meu()) return; // fechamento do socket ANTIGO: não mexe na conexão atual
      this._open = false;
      this._connecting = false;
      this._ws = null;
      if (!this._closedByUser) this._scheduleReconnect('close');
    };
    ws.onerror = () => { /* onclose cuida da reconexão */ };
  }

  /**
   * Agenda a próxima tentativa e DOBRA o passo (teto RECONNECT_MAX).
   *
   * A espera de verdade é SORTEADA dentro de ±RECONNECT_JITTER do passo. Sem jitter, N
   * telas derrubadas pela MESMA causa (queda do WiFi do evento, deploy, /reload dos
   * telões) voltavam em lockstep — todas na mesma janela de ~100ms, repetidamente,
   * batendo juntas no servidor a cada rodada. O teto continua sendo RECONNECT_MAX: no
   * topo do backoff a janela vira [70%·MAX, MAX], então o espalhamento NÃO some
   * justamente na queda longa, que é quando ele mais importa.
   */
  _scheduleReconnect(reason) {
    const passo = this._reconnectDelay;
    const min = passo * (1 - RECONNECT_JITTER);
    const max = Math.min(passo * (1 + RECONNECT_JITTER), RECONNECT_MAX);
    const espera = min + Math.random() * Math.max(0, max - min);
    this._reconnectAt = this._now() + espera;
    this._reconnectDelay = Math.min(passo * 2, RECONNECT_MAX);
    if (DEBUG) console.debug(`[RealBackend] reconecta em ${Math.round(espera)}ms (passo ${passo}ms, ${reason})`);
  }

  /**
   * A sessão RENDEU (chegou welcome/snapshot) → só AQUI o backoff volta ao início.
   * É o contrato do defeito descrito no onopen: "abriu" não basta, "abriu e o servidor
   * falou comigo" basta. Uma conexão que abre e morre em segundos continua dobrando.
   */
  _sessionEstablished() {
    this._reconnectDelay = RECONNECT_BASE;
  }

  _reconnect(reason) {
    // solta a identidade ANTES de fechar: o `onclose` que vier depois já não casa com
    // `this._ws` e não agenda uma segunda reconexão (era o que dobrava o passo duas vezes
    // por queda) nem mexe no socket que já tiver nascido no lugar
    const antigo = this._ws;
    this._ws = null;
    this._open = false;
    this._connecting = false;
    if (antigo) { try { antigo.close(); } catch (e) { /* ignora */ } }
    this._scheduleReconnect(reason);
  }

  /* --------------------------- recebimento --------------------------- */

  _onMessage(raw) {
    this._lastInbound = this._now();
    let msg;
    try { msg = proto.decode(raw); } catch (e) { return; } // JSON inválido → ignora
    if (!msg || typeof msg !== 'object') return;

    // detecção de buraco de seq (apenas alerta)
    if (typeof msg.seq === 'number') {
      if (this._inSeq != null && msg.seq > this._inSeq + 1) {
        console.warn(`[RealBackend] buraco de seq: ${this._inSeq} → ${msg.seq}`);
      }
      this._inSeq = msg.seq;
    }

    // ping/welcome são tratados na hora (fora do tick): menor latência do pong
    if (msg.type === MSG.PING) { this._send(proto.pong(msg.ts)); return; }
    if (msg.type === MSG.WELCOME) {
      this._sessionId = msg.sessionId;
      this._sessionEstablished(); // handshake concluído → a sessão é útil, backoff volta ao início
      this._syncReloadEpoch(msg); // reload perdido enquanto a aba dormia? aplica agora
      // velocidade da simulação escolhida no /vendor ({ car, person }): vem NO welcome
      // para a tela nascer com o valor certo (e reaplicar depois de uma reconexão), não
      // só no broadcast — senão uma tela aberta depois do ajuste rodaria em 1x
      if (msg.speed && this.agents?.setSpeedScale) this.agents.setSpeedScale(msg.speed);
      return;
    }
    // admin mudou os parâmetros da simulação no /vendor → aplica NA HORA, sem recarregar
    if (msg.type === MSG.SIM) {
      if (msg.speed && this.agents?.setSpeedScale) this.agents.setSpeedScale(msg.speed);
      return;
    }
    if (msg.type === MSG.PONG) return;
    // admin pediu reload do telão → recarrega já (estado vive no servidor, re-sincroniza).
    // persiste o epoch ANTES: o welcome pós-reload traz o mesmo valor → não recarrega de novo
    if (msg.type === MSG.RELOAD) {
      this._writeEpoch(msg.epoch);
      if (typeof location !== 'undefined') location.reload();
      return;
    }

    if (msg.type === MSG.BATCH && Array.isArray(msg.items)) {
      for (const it of msg.items) this._enqueue(it);
      return;
    }
    this._enqueue(msg);
  }

  _enqueue(msg) {
    const errs = proto.validate(msg);
    if (errs.length) { console.warn('[RealBackend] mensagem inválida:', msg && msg.type, errs); return; }
    this._inbox.push(msg);
  }

  /* ---------------------------- aplicação ---------------------------- */

  _apply(msg) {
    switch (msg.type) {
      case MSG.SPAWN: this._applySpawn(msg); break;
      case MSG.GOTO: this._applyGoto(msg); break;
      case MSG.ACTION: this._applyAction(msg); break;
      case MSG.EMOTE:
        if (this._effects.onEmote) this._effects.onEmote(msg.id, msg.emoji, msg.durationMs);
        else if (DEBUG) console.debug('[RealBackend] emote (sem renderer):', msg.id, msg.emoji);
        break;
      case MSG.SAY:
        if (this._effects.onSay) this._effects.onSay(msg.id, msg.text, msg.durationMs, msg.tone);
        else if (DEBUG) console.debug('[RealBackend] say (sem renderer):', msg.id, msg.text);
        break;
      case MSG.ANNOUNCE: // fala do prédio (recepcionista) → balão saindo do edifício
        if (this._effects.onAnnounce) this._effects.onAnnounce({ building: msg.building, text: msg.text, title: msg.title, tone: msg.tone, durationMs: msg.durationMs, color: msg.color });
        else if (DEBUG) console.debug('[RealBackend] announce (sem renderer):', msg.building, msg.text);
        break;
      case MSG.LEAVE: this._applyLeave(msg); break;
      case MSG.RESTYLE: this._applyRestyle(msg); break;
      case MSG.SCORE:
        if (this._effects.onScore) this._effects.onScore(msg.score, msg.missionsDone);
        break;
      case MSG.ACHIEVEMENT: // conquista → banner (Achievements) + celebração do avatar na cidade
        // `building` = o TIPO de edifício já resolvido pelo servidor (null quando ele não
        // conseguiu resolver). Repassado para quem quiser destacá-lo no banner/mapa.
        //
        // `name`/`appearance` vêm NO FRAME (servidor novo) e são QUEM celebrar. Resolver
        // isso pelo id no snapshot local não funciona quando SPAWN e ACHIEVEMENT chegam no
        // MESMO lote — que é o que o bridge emite quando o parceiro manda `achievement` sem
        // `join` antes: o inbox é aplicado no INÍCIO do tick e o snapshot dos jogadores só
        // sai no FIM dele, então o banner saía com "Jogador" e um avatar genérico
        // exatamente na rajada do evento (invisível no teste manual, onde vem
        // join → move → arrived → achievement e o id já está no snapshot).
        // Servidor ANTIGO não manda os campos → seguem undefined e o consumidor cai na
        // resolução por id (ver main.js).
        if (this._effects.onAchievement) this._effects.onAchievement({
          id: msg.id, message: msg.message, points: msg.points, building: msg.building ?? null,
          name: msg.name,
          // MESMA decomposição do spawn (_applySpawn): banner e chip consomem
          // carAppearance/personAppearance, não o `appearance` aninhado do protocolo.
          carAppearance: msg.appearance && msg.appearance.car,
          personAppearance: msg.appearance && msg.appearance.person
        });
        // avatar ESTACIONADO pula ~3s e depois sai do prédio e passa a circular (roam).
        // Navegação é client-authoritative → coreografado aqui, sem novo comando do servidor.
        if (this.agents.celebrateThenRoam) this.agents.celebrateThenRoam(msg.id, 3000);
        break;
      case MSG.SNAPSHOT: this._applySnapshot(msg); break;
      default: break;
    }
  }

  /** Troca de visual AO VIVO: atualiza a aparência no agente (sim) e dispara o efeito
   *  de transformação no renderer (reusa o giro+fumaça, mesmo p/ o mesmo tipo). */
  _applyRestyle(msg) {
    if (!msg.appearance) return;
    if (this.agents.restyle) this.agents.restyle(msg.id, msg.appearance);
    if (this._effects.onRestyle) this._effects.onRestyle(msg.id, msg.appearance);
  }

  // NOTA: depende do servidor cunhar um id ÚNICO por entrada (u_N monotônico; o `leave`
  // apaga a sessão, então uma reentrada vem com id novo — ver server/index.js). Se um id já
  // presente reaparecer (só via god-mode /cmd com id explícito), o has() abaixo o descarta.
  _applySpawn(msg) {
    if (this.agents.has && this.agents.has(msg.id)) return; // já existe → ignora duplicado
    const spec = {
      id: msg.id,
      name: msg.name,
      type: msg.kind || proto.AVATAR_KIND.CAR,
      color: msg.color,
      directed: true, // servidor decide o destino → não auto-escolhe ao entrar
      // aparência vem do servidor; se faltar, EntityManager sorteia (fallback)
      carAppearance: msg.appearance && msg.appearance.car,
      personAppearance: msg.appearance && msg.appearance.person
    };
    if (this.agents.spawn(spec) === false) this._pendingSpawns.push(spec); // boca lotada → entra no retry (tick)
    this._live.set(msg.id, this._gen); // na cidade OU na fila → passa a ser "nosso" (ver _purgeGhosts)
    this._recordFollow(msg.id, msg.followKey); // índice p/ a "visão do jogador" (após enfileirar/entrar)
  }

  /** Sai da cidade. Também PURGA um spawn ainda PENDENTE (jogador mandado embora ANTES de
   *  conseguir entrar) p/ ele nunca aparecer na cidade, e libera o índice de follow desse id.
   *  Como o tick aplica o inbox ANTES do retry de entrada, o purge acontece a tempo. */
  _applyLeave(msg) {
    const n = this._pendingSpawns.length;
    if (n) this._pendingSpawns = this._pendingSpawns.filter((s) => s.id !== msg.id);
    if (this._pendingSpawns.length !== n) this._forgetFollow(msg.id); // era só pendente → esquece o follow
    this._pendingGoto.delete(msg.id); // some com o destino guardado (se ainda não tinha entrado)
    this._live.delete(msg.id); // já mandado embora → não é candidato a fantasma no próximo snapshot
    this._destino.delete(msg.id);
    this.agents.leave(msg.id); // já na cidade → dirige p/ fora (no-op se ainda não tinha entrado)
  }

  _applyGoto(msg) {
    // último destino que ESTE viewer mandou seguir — base da reconciliação do snapshot
    // (sem isso não há como saber se o destino do servidor já é o que estamos perseguindo)
    this._destino.set(msg.id, msg.building);
    // jogador ainda na FILA de entrada (boca lotada)? guarda o destino p/ aplicar assim
    // que ele entrar. Sem isto o goto se PERDE (goto/gotoPlaza são no-op p/ id fora da
    // cidade) e o jogador entraria e sairia VAGANDO em vez de ir ao prédio que o backend
    // mandou — mais visível agora que a entrada é garantida (retry infinito). Espelha o
    // buffer de `action` (_pendingAction). Só p/ quem está pendente (evita vazar ids soltos).
    if (this.agents.has && !this.agents.has(msg.id) && this._pendingSpawns.some((s) => s.id === msg.id)) {
      this._pendingGoto.set(msg.id, { building: msg.building, mode: msg.mode });
      return;
    }
    // PRAÇA (destino especial, sem prédio): o avatar passeia pelos caminhos internos
    if (PLAZA_ID && msg.building === PLAZA_ID && this.agents.gotoPlaza) {
      this._pendingAction.delete(msg.id);
      this.agents.gotoPlaza(msg.id, msg.mode || proto.GOTO_MODE.AUTO);
      return;
    }
    const idx = BUILDING_INDEX.get(msg.building);
    if (idx == null) {
      this._send(proto.nack({ refSeq: msg.seq, code: proto.NACK_CODE.UNKNOWN_BUILDING, message: msg.building }));
      return;
    }
    this._pendingAction.delete(msg.id); // novo destino → descarta ação pendente do anterior
    this.agents.goto(msg.id, idx, msg.mode || proto.GOTO_MODE.AUTO);
  }

  _applyAction(msg) {
    const data = {
      kind: msg.kind, intent: msg.intent, outcome: msg.outcome,
      params: msg.params, durationMs: msg.durationMs, loop: msg.loop
    };
    // só "cola" no avatar quando ele está estacionado; senão guarda p/ o arrived
    if (this._parked.has(msg.id)) this.agents.actions(msg.id, data);
    else this._pendingAction.set(msg.id, data);
  }

  /**
   * Estado completo ao conectar (viewer que entra tarde). Reconstrói cada jogador
   * conforme o ESTADO no servidor, sem reanimar "entrada" de quem já está na cidade:
   *   • leaving   → nem carrega (está saindo);
   *   • entering  → carro entrando pela borda (é a 1ª entrada de fato) + destino, se houver;
   *   • parked / em trânsito (tem building) → já no prédio (placeAt);
   *   • wandering / sem destino → já dentro da cidade, vagando (placeWandering).
   * Mensagem/emoji só são reexibidos se forem RECENTES (<5s, filtrado no servidor).
   * O snapshot é o estado COMPLETO, então também EXPURGA o que sobrou de mais (fantasmas
   * de uma reconexão longa) — ver _purgeGhosts.
   */
  _applySnapshot(msg) {
    this._sessionEstablished(); // rede de segurança: servidor que mande snapshot sem welcome
    // TODO SNAPSHOT é fronteira de geração, não só a abertura do socket.
    //
    // O gateway descarta frames de uma tela lenta e, quando ela drena, manda um SNAPSHOT
    // novo NA MESMA CONEXÃO — o próprio servidor chama isso de "o único caminho de volta"
    // para uma tela que passa 8 h no ar sem reconectar. Só que o expurgo pulava todo id
    // com `gen >= this._gen`, e `_gen` só andava no `onopen`: num snapshot da mesma
    // conexão TODO avatar local tinha `gen === _gen` e o expurgo era um laço vazio.
    // MEDIDO: 5 `leave` descartados = 5 fantasmas, e 12 re-snapshots seguidos não tiraram
    // nenhum; só uma reconexão resolvia. Numa cidade de 200 o telão terminava o evento
    // com gente parada nos estandes que já tinha ido embora horas antes.
    //
    // Avançar aqui é seguro E é o que preserva o spawn EM VOO: as mensagens chegam em
    // ordem, então tudo que já está em `_live` veio ANTES deste snapshot no fluxo (e
    // portanto o servidor já o contabilizou ao montá-lo); o que vier DEPOIS nasce já na
    // geração nova e nunca é confundido com fantasma.
    this._gen++;
    const alive = new Set();                 // ids que o SERVIDOR ainda tem (base do expurgo)
    for (const p of msg.players) {
      alive.add(p.id);
      this._recordFollow(p.id, p.followKey); // índice p/ a "visão do jogador"
      if (this.agents.has && this.agents.has(p.id)) {
        this._live.set(p.id, this._gen);
        this._reconciliar(p); // já existe aqui, mas pode ter mudado de destino enquanto caímos
        continue;
      }
      if (p.state === 'leaving') continue;                    // saindo da cidade → não carrega
      this._reconstruct(p);
      this._live.set(p.id, this._gen);                        // reconstruído agora → é "nosso"
      if (p.msg) this._replayMsg(p.id, p.msg);                // só mensagens recentes
    }
    this._purgeGhosts(alive);
  }

  /**
   * FANTASMAS. O snapshot é o estado COMPLETO do servidor: quem NÃO veio nele já não existe
   * lá. Acontece com o telão que fica fora do ar por mais que o IDLE_TIMEOUT do servidor —
   * ele PERDE o broadcast de LEAVE de quem foi ceifado por timeout e, sem este expurgo, esses
   * avatares circulariam PARA SEMPRE: mandando telemetria (arrived/wandering) de ids que o
   * servidor apagou (lixo no banco) e aparecendo duplicados ao lado do próprio jogador quando
   * ele reentra (id novo). Sai pelo MESMO caminho do broadcast LEAVE (_applyLeave → dirige
   * até a borda e some off-screen), p/ não divergir dele nem apagar ninguém no meio da tela.
   * Só expurga quem nasceu numa geração ANTERIOR: um spawn ainda EM VOO (que chega depois
   * deste snapshot) nunca é fantasma, mesmo que o snapshot tenha sido montado antes dele.
   * A geração avança a cada snapshot — ver _applySnapshot.
   */
  _purgeGhosts(alive) {
    if (!this._live.size) return;
    for (const [id, gen] of [...this._live]) { // cópia: _applyLeave mexe no _live
      if (alive.has(id) || gen >= this._gen) continue;
      this._parked.delete(id);
      this._pendingAction.delete(id); // ação que nunca vai colar (o jogador não existe mais)
      this._applyLeave({ id });       // purga o pendente (spawn/goto), tira do _live e manda sair
      this._forgetFollow(id);         // a chave já foi reapontada p/ o id NOVO acima, se ele reentrou
    }
  }

  /**
   * O avatar JÁ existe aqui, mas o snapshot é o estado AUTORITATIVO do servidor — e a
   * navegação é client-authoritative, então nada reenvia os `goto` emitidos enquanto esta
   * tela esteve fora. Antes o snapshot só era usado para quem faltava e o resto era
   * ignorado: MEDIDO, um telão com 200 jogadores que ficasse 5 min fora voltava com os
   * 200 no lugar errado, para sempre — o jogador aparecia estacionado no celular dele e
   * vagando no telão. Aqui só reaplicamos o DESTINO quando ele de fato diverge.
   */
  _reconciliar(p) {
    if (p.state === 'entering') return;           // ainda entrando: o servidor não sabe mais que nós
    if (p.state === 'wandering') return;          // vagando: sem destino a impor (o goto em voo já é tratado no leave/goto)
    if (p.building == null) return;
    if (this._pendingGoto.has(p.id)) return;      // já há um destino nosso a caminho
    // aplica só se o destino do servidor difere do último que ESTE viewer mandou seguir
    if (this._destino.get(p.id) === p.building) return;
    this._applyGoto({ id: p.id, building: p.building });
  }

  _reconstruct(p) {
    // ENTRANDO → carro entrando pela borda (1ª entrada)
    if (p.state === 'entering') {
      this._spawnFromEdge(p);
      // ...e com o DESTINO, se o servidor já tiver um. O `building` é gravado no GOTO, mas o
      // `state` só sai de 'entering' com a telemetria de um viewer: do spawn até o 1º
      // arrived/wandering o jogador fica 'entering' COM building. Sem reaplicar o goto aqui,
      // quem reconstrói (a tela /me aberta na hora do join+move, ou o 1º telão de uma cidade
      // que estava SEM viewer nenhum — logo, sem telemetria e com todos em 'entering') veria o
      // avatar entrar e ficar vagando, sem nunca ir ao prédio da missão. Se o spawn caiu na
      // fila de entrada (boca lotada), o _applyGoto guarda em _pendingGoto e o tick aplica.
      if (p.building != null) this._applyGoto({ id: p.id, building: p.building });
      return;
    }
    const spec = this._placeSpec(p);
    // O `state` manda mais que o `building`: quem está VAGANDO não é colocado no prédio, ainda
    // que o servidor traga um `building` — ele é só o ÚLTIMO visitado (servidores antigos não
    // o limpam ao voltar a vagar). Sem esta guarda, todo mundo que estava circulando aparecia
    // PARADO no último prédio depois de qualquer reconexão ou /reload dos telões.
    if (p.state !== 'wandering') {
      // está na PRAÇA (building = id da praça) → reconstrói passeando nela
      if (PLAZA_ID && p.building === PLAZA_ID && this.agents.placeInPlaza && this.agents.placeInPlaza(spec)) return;
      // PARQUE de superquadra → reconstrói PASSEANDO pelo distrito. O servidor grava
      // `parked` aqui (o parque não é `plaza` no catálogo), então sem este ramo o avatar
      // voltaria imóvel em frente ao parque depois de um reload dos telões.
      const did = p.building != null ? strollDistrictOf(p.building) : null;
      if (did != null && this.agents.placeInPlaza
        && this.agents.placeInPlaza({ ...spec, building: BUILDING_INDEX.get(p.building) ?? null }, did)) return;
      // TEM destino (parado ou a caminho) → coloca já no prédio
      const idx = p.building != null ? BUILDING_INDEX.get(p.building) : null;
      if (idx != null && this.agents.placeAt && this.agents.placeAt(spec, idx)) {
        this._parked.add(p.id);
        return;
      }
    }
    // VAGANDO → já dentro da cidade, circulando (não pela borda). E se o servidor ainda
    // traz um `building`, ele é um destino EM VOO: o goto foi enviado e o avatar estava a
    // caminho quando o snapshot chegou (o servidor grava o destino no comando, mas o
    // `state` só muda com a telemetria de chegada). Sem reaplicá-lo, este viewer perdia a
    // viagem para sempre — a navegação é client-authoritative e o goto não é reenviado.
    if (this.agents.placeWandering && this.agents.placeWandering(spec)) {
      if (p.building != null) this._applyGoto({ id: p.id, building: p.building });
      return;
    }
    this._spawnFromEdge(p); // fallback
  }

  _spawnFromEdge(p) {
    this._applySpawn({ id: p.id, name: p.name, kind: p.kind, color: p.color, appearance: p.appearance });
  }

  _placeSpec(p) {
    return {
      id: p.id, name: p.name, color: p.color, directed: true,
      carAppearance: p.appearance && p.appearance.car,
      personAppearance: p.appearance && p.appearance.person
    };
  }

  /* --------------------------- reload epoch --------------------------- */
  // O welcome traz `reloadEpoch` (ts do último /reload da cidade). Guardamos o último
  // visto em sessionStorage (por ABA — dois telões da mesma cidade não se atropelam):
  // se esta tela perdeu o broadcast de reload (aba congelada pelo browser, conexão já
  // morta), o welcome da reconexão chega com epoch MAIOR que o guardado → recarrega
  // agora. Persistir antes do location.reload() é o que impede loop de reload.

  _syncReloadEpoch(msg) {
    if (typeof msg.reloadEpoch !== 'number') return; // servidor antigo → recurso desligado
    this._epochKey = 'wab.reloadEpoch:' + (msg.city || this.city || ''); // cidade JÁ normalizada pelo servidor
    const prev = this._readEpoch();
    this._writeEpoch(msg.reloadEpoch);
    if (prev != null && msg.reloadEpoch > prev && typeof location !== 'undefined') location.reload();
  }

  _readEpoch() {
    try {
      const v = sessionStorage.getItem(this._epochKey);
      return v == null ? null : Number(v);
    } catch (e) { return null; } // sem storage (Node/teste) → recurso desligado
  }

  _writeEpoch(epoch) {
    if (typeof epoch !== 'number' || !this._epochKey) return;
    try { sessionStorage.setItem(this._epochKey, String(epoch)); } catch (e) { /* ignora */ }
  }

  /** Reexibe uma mensagem/emoji recente vinda no snapshot (via os hooks de render). */
  _replayMsg(id, m) {
    if (m.type === 'emote') this._effects.onEmote && this._effects.onEmote(id, m.value);
    else if (m.type === 'say') this._effects.onSay && this._effects.onSay(id, m.value);
  }

  /* ----------------------- CityAgents → servidor ----------------------- */

  _onAgentEvent(type, a) {
    // ESPECTADOR não relata. Cada tela roda a simulação inteira da cidade e reporta TODOS
    // os avatares — redundância barata com dois ou três telões, ruína com as ~200 telas
    // /me que os jogadores abrem: o mesmo `arrived` chega 200 vezes ao gateway e 199 são
    // descarte garantido. MEDIDO: com 804 telas, o servidor satura um núcleo a ~57
    // movimentos por segundo. Quem relata é o TELÃO, que é a tela do evento; o /me só
    // assiste. (Continua recebendo tudo — o corte é só na escrita.)
    //
    // O corte tem que ser SÓ na escrita — e não era. Com o `return` no topo, a tela /me
    // também deixava de fazer a CONTABILIDADE local: `_parked` nunca era populado, e é
    // dele que depende a aplicação da `action` guardada em `_pendingAction`. Resultado
    // MEDIDO nas ~200 telas /me de cada cidade: o jogador chegava ao prédio e NUNCA
    // executava a ação — justo na tela em que ele está se assistindo. Também vazava
    // `_live` e o índice de follow numa aba de 8 h. Agora só o `_relatar` é mudo.
    if (type === 'parked') {
      this._parked.add(a.id);
      this._relatar(proto.arrived({ id: a.id, building: BUILDING_IDS[a.building] }));
      const pend = this._pendingAction.get(a.id);
      if (pend) { this._pendingAction.delete(a.id); this.agents.actions(a.id, pend); }
    } else if (type === 'plaza') {
      // chegou à praça (passeando): telemetria 'arrived' com o id da praça. Não entra
      // em `_parked` (não estaciona → não recebe `action`).
      this._parked.delete(a.id);
      this._relatar(proto.arrived({ id: a.id, building: PLAZA_ID }));
    } else if (type === 'done') {
      const d = a.actionData || {};
      this._relatar(proto.done({ id: a.id, action: d.intent || d.kind, outcome: d.outcome }));
    } else if (type === 'wandering') {
      this._parked.delete(a.id);
      this._relatar(proto.wandering({ id: a.id }));
    } else if (type === 'exited') {
      this._parked.delete(a.id);
      this._pendingAction.delete(a.id);
      this._live.delete(a.id); // saiu de fato → some do índice de fantasmas
      this._destino.delete(a.id);
      this._forgetFollow(a.id);
      this._relatar(proto.left({ id: a.id }));
    }
  }

  /** Telemetria: só o TELÃO relata. O espectador (/me) simula e desenha igual, mas não
   *  escreve — ver o cabeçalho de _onAgentEvent. */
  _relatar(msg) {
    if (this.spectator) return;
    this._send(msg);
  }

  /* -------------------- "visão do jogador" (?follow=<key>) -------------------- */

  /** Indexa a chave opaca de seguimento ↔ id interno (mais recente vence, p/ reentrada). */
  _recordFollow(id, key) {
    if (!key) return;
    this._keyById.set(id, key);
    this._idByKey.set(key, id);
  }

  /** Esquece o índice de um id que saiu (mantém o mapa enxuto numa execução de horas). */
  _forgetFollow(id) {
    const k = this._keyById.get(id);
    if (k === undefined) return;
    this._keyById.delete(id);
    if (this._idByKey.get(k) === id) this._idByKey.delete(k);
  }

  /** Resolve o identificador de seguimento (da URL /me?follow=…) para o id interno do
   *  avatar VIVO, ou null se esse jogador não está em cena agora. Aceita a chave OPACA
   *  (followKey, casada com o spawn) OU o id interno direto (god-mode sem externalId,
   *  cujo spawn não carrega followKey). */
  resolveFollow(value) {
    if (!value) return null;
    const id = this._idByKey.get(value);
    if (id != null && (!this.agents.has || this.agents.has(id))) return id;
    if (this.agents.has && this.agents.has(value)) return value; // id interno direto
    return null;
  }

  /* ------------------------------- envio ------------------------------- */

  _send(msg) {
    const frame = proto.encode(msg, { seq: this._outSeq++ });
    if (this._open && this._ws) {
      try { this._ws.send(frame); return; } catch (e) { /* cai p/ o outbox */ }
    }
    this._outbox.push(frame);
    if (this._outbox.length > 1000) this._outbox.shift(); // não cresce sem limite
  }

  _flushOutbox() {
    if (!this._open || !this._ws || !this._outbox.length) return;
    const pending = this._outbox;
    this._outbox = [];
    for (const f of pending) {
      try { this._ws.send(f); } catch (e) { this._outbox.push(f); }
    }
  }

  /** Pede ao servidor o estado completo de novo. Reabre o socket, que é o caminho já
   *  testado do `hello` → `welcome` + `snapshot`; o snapshot expurga quem não existe
   *  mais (ver _applySnapshot). Usado quando a aba volta de um sumiço longo. */
  resync(motivo = 'resync') {
    if (this._closedByUser) return;
    this._reconnect(motivo);
    this._reconnectAt = this._now(); // sem esperar o backoff: é a volta do usuário à tela
  }

  _now() {
    return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  }
}

/**
 * URL do backend, em ordem de prioridade:
 *   1. ?backend=wss://… na URL (override pontual, p/ testar)
 *   2. VITE_BACKEND embutido no build (produção — ex.: wss://app.ondigitalocean.app/ws)
 *   3. dev: mesmo host na porta 8787 (front no :5173 → backend no :8787)
 */
function defaultUrl() {
  if (typeof location === 'undefined') return 'ws://localhost:8787';
  const override = new URLSearchParams(location.search).get('backend');
  if (override) return override;
  const env = import.meta.env && import.meta.env.VITE_BACKEND;
  if (env) return env;
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const isLocal = location.hostname === 'localhost' || location.hostname === '127.0.0.1';
  // dev: front no :5173 → backend no :8787. prod: MESMO domínio, rota /ws (App Platform).
  return isLocal ? `${scheme}//${location.hostname}:8787` : `${scheme}//${location.host}/ws`;
}

/** Token do viewer (se o display for protegido): ?token= → VITE_VIEWER_TOKEN do build. */
function viewerToken() {
  if (typeof location !== 'undefined') {
    const q = new URLSearchParams(location.search).get('token');
    if (q) return q;
  }
  return (import.meta.env && import.meta.env.VITE_VIEWER_TOKEN) || undefined;
}
